// CreateAccount.jsx
import React, { useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import HtmlRenderer from 'shared-components/src/components/HtmlRenderer';
import {
  Container,
  DCard,
  DCardBody,
  HeadingGroup,
  Title,
  Subtitle,
  Field,
  TextInputWrap,
  RightIconButton,
  ReqList,
  ReqTitle,
  ReqRow,
  HelperText,
  Center,
} from './styles';
import {
  AlertCard,
  BackButton,
  Label,
  OutlineButton,
  PrimaryButton,
  ProgressBar,
  ProgressFill,
  TextInput,
} from '../style';
import { createAccount } from '../services';
import { DisbursementsContext } from '../../../contexts/DisbursementsContext';
import { CheckErrorIcon, CheckGreenIcon } from '../../../components/Icons';
import { SmallErrorRow } from '../Verification/styles';

export default function CreateAccount() {
  const navigate = useNavigate();
  const intl = useIntl();
  const { redeemToken } = useContext(DisbursementsContext);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    password_confirmation: '',
  });
  const [isLoading, setisLoading] = useState(false);
  const [account, setAccount] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [usernameError, setUsernameError] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'username' && usernameError) setUsernameError(null);
  };

  const passwordRequirements = [
    {
      id: 'req1',
      text: intl.messages['account.password.req1'],
      met: formData.password.length >= 12,
    },
    {
      id: 'req2',
      text: intl.messages['account.password.req2'],
      met: /[A-Z]/.test(formData.password),
    },
    {
      id: 'req3',
      text: intl.messages['account.password.req3'],
      met: /[a-z]/.test(formData.password),
    },
    {
      id: 'req4',
      text: intl.messages['account.password.req4'],
      met: /\d/.test(formData.password),
    },
    {
      id: 'req5',
      text: intl.messages['account.password.req5'],
      met: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password),
    },
  ];

  const isPasswordValid = passwordRequirements.every(r => r.met);
  const passwordsMatch =
    formData.password === formData.password_confirmation &&
    formData.password_confirmation !== '';
  const isUsernameValid = formData.username.length >= 3;

  const isFormValid = () =>
    isUsernameValid && isPasswordValid && passwordsMatch && !usernameError;

  const handleSumbit = async () => {
    if (isFormValid()) {
      setisLoading(true);
      setUsernameError(null);
      try {
        const response = await createAccount(formData, redeemToken);
        setAccount(response.data || null);
      } catch (err) {
        // TODO: Handle error
        const { message } = err.error;
        setUsernameError(message || 'This username is not available');
      } finally {
        setisLoading(false);
      }
    }
  };

  return (
    <>
      {/* Progress */}
      <ProgressBar>
        <ProgressFill className="seventy" />
      </ProgressBar>

      {!account && (
        <Container>
          {/* Back */}
          <BackButton type="button" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} />
            {intl.messages['common.goBack']}
          </BackButton>

          {/* Username Error Banner */}
          {usernameError && (
            <AlertCard>
              <div
                style={{
                  display: 'flex',
                  gap: '0.75rem',
                  alignItems: 'flex-start',
                }}
              >
                <CheckErrorIcon
                  width={24}
                  height={24}
                  fill="#D60000"
                  style={{ flexShrink: 0 }}
                />
                <div>
                  <h3>
                    {intl.messages['account.username.error.title'] ||
                      'Username Not Available'}
                  </h3>
                  <p>
                    {intl.messages['account.username.error.message'] ||
                      usernameError}
                  </p>
                  <OutlineButton
                    type="button"
                    onClick={() => setUsernameError(null)}
                  >
                    {intl.messages['common.dismiss'] || 'Dismiss'}
                  </OutlineButton>
                </div>
              </div>
            </AlertCard>
          )}

          {/* Heading */}
          <HeadingGroup>
            <Title>{intl.messages['account.title']}</Title>
            <Subtitle>
              {intl.messages['account.subtitle']}{' '}
              <b><HtmlRenderer html={intl.messages['account.subtitle.bold']} /></b>
            </Subtitle>
          </HeadingGroup>

          {/* Form */}
          <DCard>
            <DCardBody>
              {/* Username */}
              <Field>
                <Label htmlFor="username">
                  {intl.messages['account.username']}
                </Label>
                <TextInputWrap>
                  <TextInput
                    id="username"
                    type="text"
                    placeholder={
                      intl.messages['account.username.placeholder'] ||
                      'Choose a unique username to access your account'
                    }
                    value={formData.username}
                    onChange={e => {
                      handleInputChange('username', e.target.value);
                    }}
                    $error={!!usernameError}
                  />
                </TextInputWrap>
                {formData.username && !isUsernameValid && (
                  <SmallErrorRow>
                    <AlertCircle size={16} />
                    <span>
                      {intl.messages['account.username.invalid']}
                    </span>
                  </SmallErrorRow>
                )}
                <HelperText>
                  {intl.messages['account.username.help']}
                </HelperText>
              </Field>

              {/* Password */}
              <Field style={{ marginTop: '1.5rem' }}>
                <Label htmlFor="password">
                  {intl.messages['account.password']}
                </Label>
                <TextInputWrap>
                  <TextInput
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={intl.messages['account.password.placeholder']}
                    value={formData.password}
                    onChange={e => {
                      handleInputChange('password', e.target.value);
                    }}
                  />
                  <RightIconButton
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    aria-label={
                      showPassword ? 'Hide password' : 'Show password'
                    }
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </RightIconButton>
                </TextInputWrap>

                {formData.password && (
                  <ReqList>
                    <ReqTitle>
                      {intl.messages['account.password.requirements']}
                    </ReqTitle>
                    {passwordRequirements.map(req => (
                      <ReqRow key={req.id}>
                        {req.met ? (
                          <CheckGreenIcon
                            width={16}
                            height={16}
                            fill="#005D1F"
                          />
                        ) : (
                          <AlertCircle width={16} height={16} color="#9ca3af" />
                        )}
                        <span
                          style={{ color: req.met ? '#000000' : '#6b7280' }}
                        >
                          {req.text}
                        </span>
                      </ReqRow>
                    ))}
                  </ReqList>
                )}
              </Field>

              {/* Confirm Password */}
              <Field style={{ marginTop: '1.5rem' }}>
                <Label htmlFor="password_confirmation">
                  {intl.messages['account.confirm']}
                </Label>
                <TextInputWrap>
                  <TextInput
                    id="password_confirmation"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder={intl.messages['account.confirm.placeholder']}
                    value={formData.password_confirmation}
                    onChange={e => {
                      handleInputChange(
                        'password_confirmation',
                        e.target.value
                      );
                    }}
                  />
                  <RightIconButton
                    type="button"
                    onClick={() => setShowConfirmPassword(s => !s)}
                    aria-label={
                      showConfirmPassword ? 'Hide password' : 'Show password'
                    }
                  >
                    {showConfirmPassword ? <EyeOff /> : <Eye />}
                  </RightIconButton>
                </TextInputWrap>

                {formData.password_confirmation && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginTop: '.5rem',
                    }}
                  >
                    {passwordsMatch ? (
                      <CheckCircle color="#16a34a" />
                    ) : (
                      <AlertCircle color="#ef4444" />
                    )}
                    <span
                      style={{
                        fontSize: '.875rem',
                        color: passwordsMatch ? '#16a34a' : '#ef4444',
                      }}
                    >
                      {passwordsMatch
                        ? intl.messages['account.confirm.match']
                        : intl.messages['account.confirm.nomatch']}
                    </span>
                  </div>
                )}
              </Field>
            </DCardBody>
          </DCard>

          {/* Create Account Button */}
          <Center style={{ padding: '0 1rem', marginBottom: '4rem' }}>
            <PrimaryButton
              type="button"
              $enabled={isFormValid()}
              onClick={() => {
                handleSumbit();
              }}
              disabled={!isFormValid() || isLoading}
            >
              {intl.messages['account.button']}
            </PrimaryButton>

            {!isFormValid() && (
              <HelperText style={{ marginTop: '0.75rem' }}>
                {intl.messages['account.button.help']}
              </HelperText>
            )}
          </Center>
        </Container>
      )}

      {account && (
        <Container>
          <HeadingGroup>
            <Title>{intl.messages['account.login']}</Title>
            <Subtitle>
              <HtmlRenderer html={intl.messages['account.login.message']} />
            </Subtitle>
          </HeadingGroup>
          <Center style={{ padding: '0 1rem', marginBottom: '4rem' }}>
            <PrimaryButton
              type="button"
              onClick={() => {
                navigate('/');
              }}
              $enabled
            >
              {intl.messages['accountCreated.loginButton']}
            </PrimaryButton>
          </Center>
        </Container>
      )}
    </>
  );
}
